// The board's layers, bottom to top, and which of them answer the pointer (issue 1326).
//
// The stage is `static`, and Pixi hands an interactive parent's mode down to every child that has none of its own: a
// plain Graphics under it is hit-tested wherever it is painted. The DM's fog shade lies over the things, so a press on
// a fogged square hit the shade — a hit that is no one's — and never reached the prop (or token) standing there. Only
// the things and the grid editor's handles take presses on the canvas; every tool (fog, zones, walls, drawing, measure,
// areas, pointing) reads its presses from the canvas element itself, by board position, so its layer needs none. Every
// other layer is `none`: it is drawn and never hit, whichever tool is on.
import type { Container } from 'pixi.js';
import { shadeOrder } from './fogGeometry';

export interface WorldLayers {
  floor: Container; map: Container; pyramid: Container; tileLayer: Container; grid: Container; wallLayer: Container;
  areaLayer: Container; auraLayer: Container; focusLayer: Container; things: Container; fogLayer: Container;
  drawLayer: Container; handles: Container; overlay: Container; measureLayer: Container; measureText: Container;
  fxLayer: Container; pingLayer: Container;
}

/** The layers that take a press: the things and the grid editor's handles. */
export const PRESSABLE: ReadonlyArray<keyof WorldLayers> = ['things', 'handles'];

/** The layers in drawing order: the DM's fog shade over the things, a player's under them (see `shadeOrder`). */
export function worldOrder(l: WorldLayers, isDm: boolean): Container[] {
  const shadeAndThings = shadeOrder(isDm).map((k) => (k === 'things' ? l.things : l.fogLayer));
  return [l.floor, l.map, l.pyramid, l.tileLayer, l.grid, l.wallLayer, l.areaLayer, l.auraLayer, l.focusLayer, ...shadeAndThings,
    l.drawLayer, l.handles, l.overlay, l.measureLayer, l.measureText, l.fxLayer, l.pingLayer];
}

/** Stacks the layers into the world, every one but the pressable ones drawn only — never hit. */
export function stackWorld(world: Container, l: WorldLayers, isDm: boolean): void {
  const pressable = new Set(PRESSABLE.map((k) => l[k]));
  for (const layer of worldOrder(l, isDm)) if (!pressable.has(layer)) layer.eventMode = 'none';
  world.addChild(...worldOrder(l, isDm));
}
